/**
 * LaTeX 수식이 포함된 텍스트를 렌더링하는 컴포넌트
 *
 * - 인라인 수식: $...$ 패턴을 인식하여 KaTeX로 렌더링
 * - 일반 텍스트: 그대로 표시
 *
 * @example
 * <LatexText text="분수는 $\frac{a}{b}$ 형태로 입력하세요" />
 * // 결과: "분수는 ½ 형태로 입력하세요" (분수 수식 렌더링)
 */

import React from 'react';
import { InlineMath } from 'react-katex';
import 'katex/dist/katex.min.css';

interface LatexTextProps {
  /** LaTeX 수식이 포함된 텍스트 */
  text: string;
  /** 추가 CSS 클래스 */
  className?: string;
}

/**
 * LaTeX 특수 패턴 감지 정규식
 * - \frac, \sqrt, \sum, \int 등 LaTeX 명령어
 * - ^, _, {} 등 수식 특수문자
 */
const LATEX_PATTERN = /\\(frac|sqrt|sum|int|cdot|times|div|pm|mp|le|ge|neq|approx|alpha|beta|gamma|pi|theta|infty|partial|nabla|left|right|sin|cos|tan|log|ln|lim)|[_^{}]/;

/**
 * $...$ 패턴을 찾아서 InlineMath로 변환하는 컴포넌트
 * - $...$ 패턴: 인라인 수식으로 처리
 * - raw LaTeX (패턴 없이 \frac 등 포함): 전체를 수식으로 처리
 */
export const LatexText: React.FC<LatexTextProps> = ({ text, className }) => {
  // null/undefined 체크
  if (!text) {
    return <span className={className}></span>;
  }

  // $...$ 패턴이 없고 LaTeX 특수문자가 있으면 → 전체를 수식으로 처리
  if (!text.includes('$') && LATEX_PATTERN.test(text)) {
    try {
      return (
        <span className={className}>
          <InlineMath math={text} />
        </span>
      );
    } catch (error) {
      console.warn('LaTeX 렌더링 실패 (raw):', text, error);
      return <span className={className}>{text}</span>;
    }
  }

  // $...$ 패턴으로 텍스트 분할
  // 예: "분수 $\frac{1}{2}$ 입력" → ["분수 ", "$\frac{1}{2}$", " 입력"]
  const parts = text.split(/(\$[^$]+\$)/g);

  return (
    <span className={className}>
      {parts.map((part, index) => {
        // $로 시작하고 끝나는 부분은 LaTeX 수식
        if (part.startsWith('$') && part.endsWith('$')) {
          const latex = part.slice(1, -1); // $ 제거
          try {
            return <InlineMath key={index} math={latex} />;
          } catch (error) {
            // LaTeX 파싱 실패 시 원본 텍스트 표시
            console.warn('LaTeX 렌더링 실패:', latex, error);
            return <span key={index}>{part}</span>;
          }
        }
        // 일반 텍스트는 그대로 표시
        return <span key={index}>{part}</span>;
      })}
    </span>
  );
};
